import React, { useContext, useEffect, useRef, useState } from 'react';

import { useIsMutating } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import { Alert, Box, Button, IconButton, Tooltip, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';

import AskRichardWordmark from '~/components/brand/AskRichardWordmark';

import TourChip from '~/pages/AskRichardUnified/tour/TourChip';

import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useGetReportingLensDocumentData } from '~/hooks/apis/reporting/useGetReportingLensDocumentData';
import { lensChangeFailure, lensChangeKey } from '~/hooks/apis/reporting/useLensChange';
import useLensPublication from '~/hooks/apis/reporting/useLensPublication';
import useCurrentUser from '~/hooks/useCurrentUser';

import LensArchivedNotice from '../LensArchivedNotice';
import LensCollaborationDialog from '../LensCollaborationDialog';
import type { LensFiltered } from '../LensFilterReceipt';
import type { LensReaderScope } from '../lensReaderScope';
import type { LensEditorPanelState, LensPanelAskRenderArgs } from '../panel/panelTypes';
import { useLensSidebarOpen } from '../panel/useLensSidebarOpen';
import { SavedViewsContext } from '../personalViews/savedViewsContext';
import { useLensUndoShortcut } from '../useLensUndoShortcut';

import AddBlock from './AddBlock';
import BlockPane, { type BlockPaneView, type InspectedBlock } from './BlockPane';
import BlockSettings from './BlockSettings';
import { LINKED_BLOCK_PARAM } from './BlockView';
import DataPanel from './DataPanel';
import EditBar, { defaultViewOperations } from './EditBar';
import LensAbout from './LensAbout';
import LensActions from './LensActions';
import LensBoard from './LensBoard';
import LensDownload from './LensDownload';
import LensHeaderAttribution from './LensHeaderAttribution';
import LensPanel from './LensPanel';
import LensPublicationDialog, { type PublicationConfirmation } from './LensPublicationDialog';
import LensShare from './LensShare';
import LensStatus from './LensStatus';
import PaneEdge, { PANE_WIDTH, storedPaneWidth, useHostWidth } from './PaneEdge';
import { type PaneDesignSection, PanePresentation } from './PaneSettingsLayout';
import PaneTabs, { type PaneTab, panePanelId, paneTabId } from './PaneTabs';
import { aboutOf } from './Reading';
import { findBlock, pageBlocks } from './editor';
import { humanise } from './format';
import { readLensState, writeLensState } from './lensState';
import { hasTabs, readLensTabs, shownPage, shownTabs, writeLensTabs } from './lensTabs';
import { lensForPublication, ownsLensDraft } from './publication';
import { blockStarters, lensStarters } from './starters';
import { type LensState, asLensDocument, blockKind } from './types';
import useBoardEdit, { LENS_WIDE, boardChangeKey } from './useBoardEdit';

type LensDocumentPageProps = {
  lensId: string;
  renderAskPane?: (args: LensPanelAskRenderArgs) => React.ReactNode;
  askRichardRequest?: number;
  onOpenAskRichardPanel?: () => void;
  onLensContextChange?: (context: LensReaderScope) => void;
  onEditorPanelChange?: (state: LensEditorPanelState) => void;
  onOpenRail?: () => void;
};

const noop = () => undefined;

export default function LensDocumentPage({
  lensId,
  renderAskPane,
  askRichardRequest,
  onOpenAskRichardPanel,
  onLensContextChange,
  onEditorPanelChange,
  onOpenRail,
}: LensDocumentPageProps) {
  const theme = useTheme();
  const savedViews = useContext(SavedViewsContext);
  const narrow = useMediaQuery(theme.breakpoints.down('lg'));
  const [state, setState] = useState<LensState>(() => readLensState());
  const [tabs, setTabs] = useState<Array<string>>(() => readLensTabs());
  const [hostRef, hostWidth] = useHostWidth();
  const [paneWidth, setPaneWidth] = useState(storedPaneWidth);
  const compact = narrow || (hostWidth > 0 && hostWidth - paneWidth < PANE_WIDTH.boardLeast);
  const [askOpen, setAskOpen] = useLensSidebarOpen(compact);
  const detail = useGetReportingLeLens(lensId);
  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const publication = useLensPublication(lensId);
  const [draftShown, setDraftShown] = useState(false);
  const [confirmation, setConfirmation] = useState<PublicationConfirmation | null>(null);
  const publicationState = detail.data?.publication;
  const ownsDraft = ownsLensDraft(detail.data, user?.email);
  const draftLocked = Boolean(publicationState?.editorEmail) && !ownsDraft;
  const showingDraft = Boolean(
    publicationState &&
    (publicationState.publishedVersion === null || (draftShown && publicationState.draftVersion !== null)),
  );
  const lens = lensForPublication(detail.data, showingDraft);
  const document = asLensDocument(lens?.spec);
  const canEdit = Boolean(detail.data?.access.canEdit);
  const shown = document && hasTabs(document.page) ? shownTabs(document.page, tabs) : undefined;
  const { data, isFetching, error, isPlaceholderData, settling, waiting, refresh } = useGetReportingLensDocumentData(
    document && !lens?.archivedAt ? lensId : undefined,
    lens?.version,
    state,
    shown,
  );
  const unplanned =
    !isFetching && data && !Object.keys(data.blocks).length && data.problems.length
      ? [...new Set(data.problems.map((problem) => problem.message))].join(' ')
      : undefined;
  const failure = (!isFetching && error?.message) || unplanned;
  const askVisible = Boolean(renderAskPane) && askOpen;
  const [editing, setEditing] = useState(false);
  const [finishing, setFinishing] = useState<false | 'draft' | 'published'>(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<string>();
  const [refreshedBlock, setRefreshedBlock] = useState<string>();
  const [paneTab, setPaneTab] = useState<PaneTab>('ask');
  const readingPane = useRef<{ tab: PaneTab; open: boolean }>({ tab: 'ask', open: askOpen });
  const [paneSection, setPaneSection] = useState<PaneDesignSection>('data');
  const [askedBlock, setAskedBlock] = useState<{ id: string; title: string }>();
  const [inspected, setInspected] = useState<InspectedBlock>();
  const [draft, setDraft] = useState<{ requestId: number; text: string }>();
  const editor = useBoardEdit(lensId, lens?.version);
  const building = editing && !preview && (!publicationState || ownsDraft);
  const settingsOpen = building;
  const visiblePaneTab = !building && paneTab === 'block' ? 'lens' : paneTab;
  useLensUndoShortcut(lensId, canEdit && (!publicationState || (showingDraft && ownsDraft)));
  const saving = useIsMutating({ mutationKey: boardChangeKey(lensId) });
  const changing = useIsMutating({ mutationKey: lensChangeKey(lensId) }) - saving;
  const [selectedId, selectedItem] = selected?.includes('/')
    ? [selected.slice(0, selected.indexOf('/')), selected.slice(selected.indexOf('/') + 1)]
    : [selected, undefined];
  const selectedBlock = settingsOpen && document ? findBlock(document.page, selectedId) : undefined;
  const selectedTitle = selectedBlock
    ? String(selectedBlock.title ?? data?.blocks[selectedBlock.id]?.name ?? selectedBlock.id)
    : '';
  const paneShown = askOpen;
  const askedBlockSpec = document ? findBlock(document.page, askedBlock?.id) : undefined;

  useEffect(() => writeLensState(state), [state]);
  useEffect(() => {
    setEditing(false);
    setDraftShown(false);
    setPreview(false);
  }, [lensId]);
  // A successful structural chat change joins the same shared draft. Ordinary view filters never change its version.
  const lastDraft = useRef<{ lensId: string; version: number | null }>(undefined);
  useEffect(() => {
    if (!publicationState) return;
    const previous = lastDraft.current;
    const version = publicationState.draftVersion;
    lastDraft.current = { lensId, version };
    if (ownsDraft && version !== null && previous?.lensId === lensId && previous.version !== version)
      setDraftShown(true);
    if (ownsDraft && previous?.lensId !== lensId && publicationState.hasUnpublishedChanges) setDraftShown(true);
    if (!ownsDraft || version === null) {
      setEditing(false);
      setPreview(false);
    }
  }, [lensId, publicationState, ownsDraft]);
  const linkedBlock = useRef(new URLSearchParams(window.location.search).get(LINKED_BLOCK_PARAM));
  useEffect(() => {
    const id = linkedBlock.current;
    const node =
      id && data ? window.document.querySelector<HTMLElement>(`[data-lens-block="${CSS.escape(id)}"]`) : null;
    if (!node) return undefined;
    linkedBlock.current = null;
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    node.classList.add('linked');
    const timer = window.setTimeout(() => node.classList.remove('linked'), 2600);
    return () => window.clearTimeout(timer);
  }, [data]);
  useEffect(() => {
    const apply = (event: Event) => {
      const receipt = (event as CustomEvent<LensFiltered>).detail;
      if (!receipt || receipt.lensId !== lensId) return;
      setState((current) =>
        receipt.clear
          ? {}
          : {
              ...current,
              ...(receipt.state?.period ? { period: receipt.state.period } : {}),
              filters: { ...current.filters, ...receipt.state?.filters },
            },
      );
    };
    window.addEventListener('lens-view-filter', apply);
    return () => window.removeEventListener('lens-view-filter', apply);
  }, [lensId]);
  useEffect(() => writeLensTabs(tabs), [tabs]);
  useEffect(() => {
    if (askRichardRequest) {
      setAskOpen(true);
    }
  }, [askRichardRequest, setAskOpen]);
  useEffect(() => {
    onEditorPanelChange?.({ open: paneShown, width: paneShown ? paneWidth : 0 });
  }, [paneShown, paneWidth, onEditorPanelChange]);
  useEffect(() => {
    if (!data || isPlaceholderData) {
      onLensContextChange?.({ lensId, ready: false });
      return;
    }
    const labels = new Map(
      Object.values(data.queries).flatMap((envelope) =>
        envelope.columns.map((column) => [column.name, column.label] as const),
      ),
    );
    onLensContextChange?.({
      lensId,
      ready: true,
      period: {
        from: data.period.current.from,
        to: data.period.current.to,
        summary: `${data.period.label} (${data.period.current.label})`,
      },
      filters: [...Object.entries(state.filters ?? {}), ...Object.entries(state.select ?? {})].map(
        ([column, values]) => ({ label: labels.get(column) ?? column, value: values.join(', ') }),
      ),
    });
  }, [data, isPlaceholderData, lensId, onLensContextChange, state]);

  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]')) return;
      if (preview) {
        if (event.key === 'Escape') setPreview(false);
        return;
      }
      if (event.key === 'Escape') {
        if (selected) setSelected(selectedItem ? selectedId : undefined);
        else finishEditing();
      }
      if (selected && (event.key === 'Delete' || event.key === 'Backspace')) {
        event.preventDefault();
        editor.apply({ op: 'remove', id: selected });
        setSelected(selectedItem ? selectedId : undefined);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, preview, selected, selectedId, selectedItem, editor]);

  useEffect(() => {
    if (!canEdit || editing) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'e' || event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      if (
        (event.target as HTMLElement).closest(
          'input, textarea, select, button, a, [contenteditable="true"], [role="dialog"], [role="menu"], [role="tab"]',
        )
      )
        return;
      event.preventDefault();
      startEditing();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, editing, publicationState, publication.isPending]);

  const picking = !building && Object.keys(state.select ?? {}).length > 0;
  const reading = !building && Boolean(inspected);
  useEffect(() => {
    if (!picking && !reading) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        (event.target as HTMLElement).closest(
          'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]',
        )
      )
        return;
      if (reading) {
        setInspected(undefined);
        setSelected(undefined);
      } else setState(({ select: _cleared, ...rest }) => rest);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picking, reading]);

  const startEditing = async () => {
    if (publicationState) {
      if (draftLocked) {
        setDraftShown(true);
        setEditing(false);
        return;
      }
      try {
        await publication.mutateAsync({ action: 'start', baseVersion: detail.data!.lens.version });
        setDraftShown(true);
      } catch (error) {
        enqueueSnackbar(lensChangeFailure(error), { variant: 'error' });
        return;
      }
    }
    if (!editing) readingPane.current = { tab: paneTab, open: askOpen };
    setEditing(true);
    setPreview(false);
    setInspected(undefined);
    setSelected(undefined);
    setPaneTab('lens');
    setAskOpen(true);
  };
  const finishEditing = (shown: 'draft' | 'published' = 'draft') => {
    const settled = editor.status !== 'saving' && !editor.pending && !detail.isFetching;
    setFinishing(settled ? false : shown);
    if (!settled) return;
    setEditing(false);
    setPreview(false);
    setInspected(undefined);
    setSelected(undefined);
    setPaneTab(readingPane.current.tab);
    setAskOpen(readingPane.current.open);
    if (shown === 'published' && publicationState?.publishedVersion !== null) setDraftShown(false);
  };
  useEffect(() => {
    if (finishing) finishEditing(finishing);
  }, [finishing, editor.status, editor.pending, detail.isFetching]);
  const viewPublished = () => {
    if (publicationState && (editor.status === 'saving' || editor.pending)) {
      enqueueSnackbar('Your changes are still saving. Please wait before leaving the draft.', { variant: 'info' });
      return;
    }
    finishEditing('published');
  };
  const editBlock = async (blockId: string) => {
    await startEditing();
    if (draftLocked) return;
    setPreview(false);
    setInspected(undefined);
    setSelected(blockId);
    setPaneTab('block');
    setAskOpen(true);
  };
  const select = (blockId: string | undefined) => {
    setSelected(blockId);
    if (!blockId) setInspected(undefined);
    if (blockId && building) {
      setPaneTab('block');
      setAskOpen(true);
    }
  };
  const [composerFocus, setComposerFocus] = useState(0);
  useEffect(() => {
    if (!composerFocus) return undefined;
    const frame = window.requestAnimationFrame(() =>
      window.document.querySelector<HTMLElement>(`#${panePanelId('ask')} textarea`)?.focus(),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [composerFocus]);
  const showAsk = () => {
    if (!askVisible) onOpenAskRichardPanel?.();
    setPaneTab('ask');
    setAskOpen(true);
  };
  const askPressed = paneShown && paneTab === 'ask';
  const toggleAsk = () => {
    if (askPressed) return setAskOpen(false);
    showAsk();
    setComposerFocus(Date.now());
  };
  const inspect = (block: { id: string; title: string }, view: BlockPaneView) => {
    if (settingsOpen) select(block.id);
    setInspected({ ...block, view, asked: Date.now() });
    setPaneTab(building ? 'block' : 'lens');
    setAskOpen(true);
  };
  const filterLabel = (column: string) =>
    document?.filters.find((filter) => filter.column === column)?.label ?? humanise(column);
  const aboutBlock = (id: string) => {
    const spec = document ? findBlock(document.page, id) : undefined;
    const planned = data?.blocks[id];
    const query = planned?.query ?? planned?.queries?.[0];
    return spec ? aboutOf(spec, query ? data?.queries[query] : undefined) : undefined;
  };
  const blockPane = (block: InspectedBlock, embedded = false) =>
    lens ? (
      <BlockPane
        about={aboutBlock(block.id)}
        block={block}
        data={data}
        document={document}
        embedded={embedded}
        lensId={lensId}
        lensVersion={lens.version}
        onClose={() => setInspected(undefined)}
        onEdit={canEdit && document ? () => editBlock(block.id) : undefined}
        onView={(view) => setInspected({ ...block, view, asked: Date.now() })}
        state={state}
      />
    ) : null;
  const railButton = onOpenRail ? (
    <IconButton aria-label="Open threads and Lenses" onClick={onOpenRail} size="small">
      <MenuIcon fontSize="small" />
    </IconButton>
  ) : null;
  const onBand = document?.display.tone === 'accent';
  const filled = !onBand && !building;
  const tokens = lensTokens(theme);
  const publishFill = theme.palette.mode === 'dark' ? tokens.accent : alpha(tokens.accent, 0.82);
  const publishSx = {
    bgcolor: publishFill,
    color: tokens.accentContrast,
    '&:hover': { bgcolor: alpha(tokens.accent, 0.9) },
    '&.Mui-disabled': { bgcolor: publishFill, color: tokens.accentContrast, opacity: 0.4 },
  };
  const askButton = (narrow: boolean) =>
    renderAskPane ? (
      narrow ? (
        <Tooltip title={askPressed && !editing ? 'Hide askRichard' : 'Ask about this Lens'}>
          <IconButton
            aria-label={askPressed && !editing ? 'Hide askRichard' : 'Ask about this Lens'}
            aria-pressed={askPressed}
            onClick={toggleAsk}
            size="small"
          >
            <AutoAwesomeOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          aria-label={askPressed && !editing ? 'Hide askRichard' : 'Open askRichard'}
          aria-pressed={askPressed}
          onClick={toggleAsk}
          size="small"
          startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
          sx={{
            whiteSpace: 'nowrap',
            ...(onBand ? { color: 'common.white', borderColor: 'rgba(255, 255, 255, 0.6)' } : {}),
          }}
          variant={filled ? 'contained' : 'outlined'}
        >
          <AskRichardWordmark inverted={onBand || filled || theme.palette.mode === 'dark'} size={14} />
        </Button>
      )
    ) : null;
  const csvBlocks = document
    ? pageBlocks(shownPage(document.page, tabs))
        .map((block) => block.id)
        .filter((id) => data?.blocks[id]?.query || data?.blocks[id]?.queries?.length)
    : [];
  const confirmPublication = async (action: PublicationConfirmation) => {
    await publication.mutateAsync({ action, baseVersion: detail.data!.lens.version });
    if (action === 'takeover') {
      setDraftShown(true);
      setEditing(true);
      setPreview(false);
    } else {
      finishEditing();
      setDraftShown(false);
      enqueueSnackbar(action === 'publish' ? 'Published' : 'Draft discarded', { variant: 'success' });
    }
  };
  const displayedDetail = detail.data && lens ? { ...detail.data, lens } : undefined;
  const tools = (narrow: boolean) => ({
    savedViews:
      savedViews && !showingDraft ? (
        <Tooltip title="Saved views">
          <IconButton aria-haspopup="dialog" aria-label="Saved views" onClick={savedViews.open} size="small">
            <BookmarkBorderOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null,
    ask: askButton(narrow),
    share: displayedDetail ? (
      <LensShare
        detail={displayedDetail}
        narrow={narrow}
        state={state}
        publishingDisabled={
          publication.isPending || editor.status === 'saving' || editor.pending || Boolean(saving || changing)
        }
      />
    ) : null,
    download: lens ? (
      <LensDownload
        blocks={csvBlocks}
        lensId={lensId}
        lensVersion={lens.version}
        state={state}
        tabs={shown}
        title={lens.title}
      />
    ) : null,
    spill: displayedDetail ? (
      <>
        {publicationState && canEdit && !preview ? (
          <LensStatus
            draftShown={showingDraft}
            onDiscard={() => setConfirmation('discard')}
            onPublish={() => {
              setDraftShown(true);
              setConfirmation('publish');
            }}
            onShowDraft={(shown) => (shown ? setDraftShown(true) : viewPublished())}
            onTakeOver={() => setConfirmation('takeover')}
            ownsDraft={ownsDraft}
            pending={publication.isPending || editor.status === 'saving' || editor.pending}
            publication={publicationState}
            publishSx={publishSx}
          />
        ) : null}
        {canEdit && !editing && !preview ? (
          <Tooltip title="Edit (E)">
            <IconButton
              aria-label="Edit"
              disabled={publication.isPending}
              onClick={() => void startEditing()}
              size="small"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <LensActions
          asReader={preview}
          data={data}
          detail={displayedDetail}
          onRefresh={() => void refresh()}
          refreshing={isFetching}
          state={state}
          publication={
            publicationState && canEdit
              ? {
                  headDetail: detail.data!,
                  draftShown: showingDraft,
                }
              : undefined
          }
        />
      </>
    ) : null,
    rail: narrow ? railButton : null,
    tourChip: editing ? null : <TourChip />,
  });

  if (detail.data?.lens.archivedAt) return <LensArchivedNotice detail={detail.data} />;

  if (!lens || !document) {
    return (
      <Box aria-busy role={detail.error ? 'alert' : undefined} sx={{ color: 'text.secondary', fontSize: 13, p: 3 }}>
        {detail.error ? detail.error.message : null}
      </Box>
    );
  }

  const defaults = defaultViewOperations(document, state);
  const editBar =
    editing && (!publicationState || ownsDraft) ? (
      <EditBar
        history={editor}
        lensSettingsOpen={paneShown && paneTab === 'lens'}
        onAddBlock={setAddAnchor}
        onDone={() => finishEditing()}
        onLensSettings={() => {
          setPaneTab('lens');
          setAskOpen(true);
        }}
        onMakeDefault={
          defaults.operations.length
            ? () => {
                const selected = state;
                editor.apply(defaults.operations, (saved) => {
                  if (!saved) return;
                  setState((current) => {
                    const next = { ...current };
                    if (current.period === selected.period) delete next.period;
                    const filters = { ...current.filters };
                    for (const column of defaults.filters) {
                      if (JSON.stringify(filters[column]) === JSON.stringify(selected.filters?.[column]))
                        delete filters[column];
                    }
                    next.filters = filters;
                    return next;
                  });
                });
              }
            : undefined
        }
        onPreview={(on) => {
          setPreview(on);
          setSelected(undefined);
          setInspected(undefined);
        }}
        preview={preview}
      />
    ) : undefined;

  return (
    <Box ref={hostRef} sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {detail.data ? (
        <LensPublicationDialog
          action={confirmation}
          detail={detail.data}
          onClose={() => setConfirmation(null)}
          onConfirm={confirmPublication}
          pending={publication.isPending || editor.status === 'saving' || editor.pending}
        />
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        {showingDraft && draftLocked ? (
          <Alert
            action={
              <Button disabled={publication.isPending} onClick={() => setConfirmation('takeover')} size="small">
                Take over editing
              </Button>
            }
            severity="info"
            sx={{ m: 2, mb: 0 }}
          >
            {publicationState?.editorEmail} is editing this draft.
          </Alert>
        ) : null}
        <LensBoard
          attribution={
            lens && detail.data ? (
              <>
                <LensHeaderAttribution
                  lens={lens}
                  attribution={detail.data.attribution}
                  viewCount={detail.data.viewCount}
                  onHistory={canEdit && !preview ? () => setHistoryOpen(true) : undefined}
                />
                <LensCollaborationDialog
                  lens={lens}
                  access={detail.data.access}
                  open={historyOpen}
                  onClose={() => setHistoryOpen(false)}
                />
              </>
            ) : undefined
          }
          data={data}
          document={document}
          editor={building ? editor : undefined}
          failed={failure || undefined}
          retry={() => void refresh()}
          loading={isFetching && isPlaceholderData}
          applying={changing > 0 || editor.pending || settling}
          fading={
            waiting
              ? [LENS_WIDE]
              : refreshedBlock && isFetching
                ? [refreshedBlock]
                : changing > 0 || saving > 0 || settling || isFetching
                  ? editor.touched
                  : undefined
          }
          askAbout={
            renderAskPane
              ? (block, text) => {
                  setAskedBlock(block);
                  if (text) setDraft({ requestId: Date.now(), text });
                  showAsk();
                  setComposerFocus(Date.now());
                }
              : undefined
          }
          askFix={
            renderAskPane
              ? (text) => {
                  setAskedBlock(undefined);
                  setDraft({ requestId: Date.now(), text });
                  showAsk();
                }
              : undefined
          }
          asked={inspected?.id ?? askedBlock?.id}
          follow={(block) => {
            if (settingsOpen) setSelected(block.id);
            setInspected((current) => (current?.id === block.id ? current : { ...block, view: 'about' }));
          }}
          inspect={inspect}
          editBlock={canEdit ? editBlock : undefined}
          refreshBlock={(blockId) => {
            setRefreshedBlock(blockId);
            void refresh(blockId).finally(() => setRefreshedBlock(undefined));
          }}
          refreshing={isFetching}
          refreshedBlock={refreshedBlock}
          certified={!showingDraft && detail.data?.certification?.badge.kind === 'certified'}
          editBar={editBar}
          onStateChange={setState}
          onTabsChange={setTabs}
          state={state}
          tabs={tabs}
          theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
          tools={tools}
          selected={selected}
          onSelect={select}
        />
      </Box>
      {building ? (
        <AddBlock
          anchor={addAnchor}
          data={data}
          document={document}
          editor={editor}
          onClose={() => setAddAnchor(null)}
        />
      ) : null}
      {paneShown ? (
        <Box
          sx={{
            borderLeft: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            width: paneWidth,
            maxWidth: '100%',
            flexShrink: 0,
            bgcolor: 'background.paper',
            position: 'relative',
            ...(compact
              ? { position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: theme.zIndex.drawer, boxShadow: 8 }
              : {}),
            '@media print': { display: 'none' },
          }}
        >
          {compact ? null : (
            <PaneEdge
              most={Math.max(PANE_WIDTH.least, Math.min(PANE_WIDTH.most, hostWidth - PANE_WIDTH.boardLeast))}
              onCollapse={() => setAskOpen(false)}
              onWidth={setPaneWidth}
              width={paneWidth}
            />
          )}
          <PaneTabs
            onCollapse={() => setAskOpen(false)}
            onTab={setPaneTab}
            tab={visiblePaneTab}
            withAsk={Boolean(renderAskPane)}
            withBlock={building}
          />
          {building ? (
            <Box
              aria-labelledby={paneTabId('block')}
              hidden={visiblePaneTab !== 'block'}
              id={panePanelId('block')}
              role="tabpanel"
              sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
            >
              {building || (settingsOpen && selectedBlock) ? (
                <PanePresentation
                  active={building && paneTab === 'block' && !selectedItem}
                  emptyHint={
                    selectedBlock ? 'No setting matches this block.' : 'Select a block to search its settings.'
                  }
                  onSection={setPaneSection}
                  section={paneSection}
                >
                  <BlockSettings
                    block={selectedBlock}
                    data={data}
                    editor={editor}
                    lens={document}
                    family={selectedId ? data?.blocks[selectedId]?.family : undefined}
                    item={selectedItem}
                    view={
                      selectedBlock && typeof selectedBlock.view === 'string'
                        ? document?.views[selectedBlock.view]
                        : undefined
                    }
                    siblings={document ? pageBlocks(document.page) : []}
                    kind={
                      selectedBlock ? (blockKind(selectedBlock) ?? data?.blocks[selectedBlock.id]?.kind) : undefined
                    }
                    onSelect={select}
                    state={state}
                    howItsMade={
                      selectedBlock && !selectedItem
                        ? blockPane(
                            inspected?.id === selectedBlock.id
                              ? inspected
                              : { id: selectedBlock.id, title: selectedTitle, view: 'about' },
                            true,
                          )
                        : undefined
                    }
                  />
                </PanePresentation>
              ) : inspected ? (
                blockPane(inspected)
              ) : (
                <Typography color="text.secondary" sx={{ fontSize: 13, p: 2 }}>
                  Select a block on the board.
                </Typography>
              )}
            </Box>
          ) : null}
          <Box
            aria-labelledby={paneTabId('lens')}
            hidden={visiblePaneTab !== 'lens'}
            id={panePanelId('lens')}
            role="tabpanel"
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
          >
            {settingsOpen ? (
              <PanePresentation
                active={building && paneTab === 'lens'}
                emptyHint="No Lens setting matches."
                onSection={setPaneSection}
                section={paneSection}
              >
                <LensPanel
                  data={data}
                  dataPanel={<DataPanel data={data} editor={editor} lensId={lensId} lensVersion={lens.version} />}
                  document={document}
                  editor={editor}
                />
              </PanePresentation>
            ) : inspected ? (
              blockPane(inspected)
            ) : (
              <LensAbout data={data} document={document} filterLabel={filterLabel} state={state} />
            )}
          </Box>
          {renderAskPane ? (
            <Box
              aria-labelledby={paneTabId('ask')}
              id={panePanelId('ask')}
              role="tabpanel"
              sx={{
                display: visiblePaneTab !== 'ask' ? 'none' : 'flex',
                flex: 1,
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              {renderAskPane({
                onApplyProposal: noop,
                onSaveDraft: async () => undefined,
                dirty: false,
                isSaving: false,
                lensTitle: lens.title,
                canEdit: canEdit && !draftLocked,
                onClearCard: () => setAskedBlock(undefined),
                block: askedBlock,
                draft,
                starters: askedBlockSpec ? blockStarters(askedBlockSpec, data, document) : lensStarters(document, data),
              })}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
